/**
 * Step 3: Governance & Current Software (Consolidated)
 * Combines previous Steps 3 & 4 to show the community's current state in one place.
 */

import React from 'react';
import { Users, Layout, AlertCircle, HardDrive } from 'lucide-react';
import { StepComponentProps } from '../../../../../types/onboarding';
import { GOVERNANCE_OPTIONS, CURRENT_SOFTWARE_OPTIONS } from '../../../../../constants/onboarding';

export const Step3CurrentState: React.FC<StepComponentProps> = ({ formData, onFieldChange }) => {
  const handleGovernanceChange = (structure: string, checked: boolean) => {
    const current = (formData.governance as string[]) || [];
    if (checked) {
      onFieldChange('governance', [...current, structure]);
    } else {
      onFieldChange(
        'governance',
        current.filter((s) => s !== structure),
      );
    }
  };

  const handleSoftwareChange = (software: string, checked: boolean) => {
    const current = (formData.currentSoftware as string[]) || [];
    if (checked) {
      onFieldChange('currentSoftware', [...current, software]);
    } else {
      onFieldChange(
        'currentSoftware',
        current.filter((s) => s !== software),
      );
    }
  };

  const handlePainPointChange = (point: string, checked: boolean) => {
    const current = (formData.painPoints as string[]) || [];
    if (checked) {
      onFieldChange('painPoints', [...current, point]);
    } else {
      onFieldChange(
        'painPoints',
        current.filter((p) => p !== point),
      );
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <HardDrive className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Current Community State</h2>
        <p className="text-lg text-slate-600">
          Tell us about your current governance and technology landscape
        </p>
      </div>

      {/* Section 1: Governance */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-6 h-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-slate-800">Governance Structure</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {GOVERNANCE_OPTIONS.map((option) => (
            <label
              key={option}
              className="flex items-center p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={(formData.governance as string[])?.includes(option) || false}
                onChange={(e) => handleGovernanceChange(option, e.target.checked)}
                className="mr-3 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-slate-700">{option}</span>
            </label>
          ))}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Years of Governance Experience *
            </label>
            <select
              value={formData.governanceExperience || ''}
              onChange={(e) => onFieldChange('governanceExperience', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select experience level...</option>
              <option value="none">No previous experience</option>
              <option value="1-2">1-2 years</option>
              <option value="3-5">3-5 years</option>
              <option value="5+">5+ years</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Time Commitment Preference *
            </label>
            <select
              value={formData.timeCommitment || ''}
              onChange={(e) => onFieldChange('timeCommitment', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select preference...</option>
              <option value="minimal">Minimal (&lt; 2 hours/month)</option>
              <option value="standard">Standard (2-5 hours/month)</option>
              <option value="active">Active (5-10 hours/month)</option>
              <option value="very-active">Very Active (10+ hours/month)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Section 2: Current Software */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs">
        <div className="flex items-center gap-2 mb-4">
          <Layout className="w-6 h-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-slate-800">Current Software Solutions</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {CURRENT_SOFTWARE_OPTIONS.map((software) => (
            <label
              key={software}
              className="flex items-center p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={(formData.currentSoftware as string[])?.includes(software) || false}
                onChange={(e) => handleSoftwareChange(software, e.target.checked)}
                className="mr-3 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-slate-700 text-sm">{software}</span>
            </label>
          ))}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Primary Pain Points & Challenges *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              'Communication delays',
              'Lack of transparency',
              'Financial management complexity',
              'Manual administrative tasks',
              'Member engagement issues',
              'Maintenance tracking issues',
              'Document management problems',
              'Meeting coordination',
            ].map((point) => (
              <label key={point} className="flex items-center p-2 hover:bg-slate-50 rounded">
                <input
                  type="checkbox"
                  checked={(formData.painPoints as string[])?.includes(point) || false}
                  onChange={(e) => handlePainPointChange(point, e.target.checked)}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">{point}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
        <p className="text-sm text-blue-800">
          Understanding your current state helps us demonstrate how Community App specifically
          improves your existing workflows.
        </p>
      </div>
    </div>
  );
};
